/**
 * Runs one task at a time per key, keeping only the newest task that arrives
 * while one is running.
 *
 * The add-on assets are replaced whole, so two writes to one asset cannot be
 * ordered from the client: whichever the backend applies last wins, and this
 * side never learns which. Only the newest waiting task is kept for the same
 * reason — each one sends the entire asset, so an older one queued behind it
 * would ship stale content over a newer edit.
 *
 * The queue is module-scoped rather than a ref because the editors unmount on
 * every rail switch and flush a write on the way out. A per-mount guard is
 * gone by the time the card comes back, so the remounted card would write
 * straight over the request still open from the last one.
 *
 * Tasks are expected to settle rather than reject — the caller reports its own
 * failures. A rejection is still contained, so it can't strand the queue.
 */
const queues = new Map<
  string,
  { running: boolean; next?: () => Promise<void> }
>();

/**
 * Test-only. The map outlives any one render by design, so a test that ends
 * with a write still open leaves its key marked busy and every later test
 * writing that asset waits behind a request that will never settle. Call this
 * between tests; nothing in the app should.
 */
export const resetSerializedKeys = (): void => queues.clear();

export const serializeByKey = async (
  key: string,
  task: () => Promise<void>
): Promise<void> => {
  const queue = queues.get(key) ?? { running: false };
  queues.set(key, queue);
  if (queue.running) {
    queue.next = task;
    return;
  }

  queue.running = true;
  let current: (() => Promise<void>) | undefined = task;
  try {
    while (current) {
      await current();
      current = queue.next;
      queue.next = undefined;
    }
  } finally {
    // Dropped rather than left idle: a key is one asset, and the editor for it
    // is usually gone by now.
    queues.delete(key);
  }
};
